import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../../contexts/GlobalContext";
import { array, boolean, number, object, setLocale, string } from "yup";
import { en } from "yup-locales";
import { useFormik } from "formik";
import {
  _ACTIONS,
  _COLORS,
  _HTTP,
  _MESSAGES,
  _SWAL_ICONS,
} from "../../../utils/constants";
import { _ROUTES } from "../../../utils/_routes_constants";
import { RequestHelper } from "../../../utils/requestHelper";
import { createConfirmToast, createToast } from "../../../utils/toast";
import { BasicListContext } from "../../../contexts/BasicListContext";
import { getMuiTableConfig } from "../../../utils/MuiTableUtils/muiTableBuilder";
import { useMaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import { MuiTableComponent } from "../../helpers/MuiTableComponent";
import BasicModal from "../../helpers/BasicModal";
import { BasicFormikForm } from "../../form/BasicFormikForm";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { buildOptions } from "../../../utils/formHelper";
import { AccountsListColumns } from "../../../utils/MuiTableUtils/ColumnBuilders/AccountsListColumns";
import { MentionInput } from "../../form/MentionInput";
import { DataService } from "../../../services/DataService";

export const AccountsList = ({
  editData,
  setEditData,
  showModal,
  setShowModal,
}) => {
  const [validationSchema, setValidationSchema] = useState(null);
  const [customStructure, setCustomStructure] = useState(null);

  const rq = new RequestHelper();
  const dataService = new DataService();
  const { open } = useContext(GlobalContext);
  const [options, setOptions] = useState({
    type: [],
    activity: [],
    accountParameters: [],
  });

  setLocale(en);

  const formik = useFormik({
    initialValues: {
      name: "",
      accountNumber: null,
      apOrder: null,
      type: "",
      activity: "",
      active: true,
      display: true,
      lineAbove: false,
      export: true,
      apGroup: false,
      useFormula: false,
      displayChildren: false,
      accountParameters: [],
      formula: "",
      colorText: "#000000",
      colorBackground: "#ffffff",
    },
    onSubmit: (values) => {
      const methode = editData ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = _ROUTES.ACCOUNTS + (editData ? `/${editData.id}` : "");
      const account = {
        ...values,
      };

      if (account.accountNumber === "") account.accountNumber = null;
      if (account.apOrder === "") account.apOrder = null;
      if (account.accountParameters.length > 0) {
        account.accountParameters = account.accountParameters.map(
          (el) => el.value,
        );
      }

      rq.doRequest(methode, url, account)
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            setShowModal(false);
            setEditData(null);
            setRefresh((prev) => {
              return prev + 1;
            });
            createToast(_SWAL_ICONS.success, "Account saved successfully");
          }
        })
        .catch((err) => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
    validationSchema: validationSchema,
  });

  // Generate the validation schema based on current values
  const updateValidationSchema = () => {
    const validationSchemaObject = {
      name: string().required().label("Name"),
      accountNumber: string().label("Account Number").nullable(true),
      apOrder: number().nullable(true).label("Order").nullable(true),
      type: string().required().label("Type"),
      activity: string().label("Activity"),
      active: boolean().label("Active"),
      display: boolean().label("Display"),
      lineAbove: boolean().label("Line Above"),
      export: boolean().label("Export"),
      apGroup: boolean().label("Grouping"),
      useFormula: boolean().label("Use Formula"),
      displayChildren: boolean().label("Display Children"),
      accountParameters: array().label("Account Associate"),
      formula: string().label("Formula").nullable(true),
      colorText: string().label("Text Color"),
      colorBackground: string().label("Background Color"),
    };

    // Conditionally remove fields based on apGroup value
    if (!formik.values.apGroup) {
      delete validationSchemaObject.useFormula;
      delete validationSchemaObject.displayChildren;
      delete validationSchemaObject.accountParameters;
      delete validationSchemaObject.formula;
    }

    if (!formik.values.useFormula) {
      delete validationSchemaObject.formula;
    } else {
      delete validationSchemaObject.accountParameters;
    }

    setValidationSchema(object(validationSchemaObject));
  };

  const getCustomStructure = () => {
    const structure = {
      0: ["name"],
      1: ["accountNumber", "apOrder"],
      1.5: ["type"],
      2: ["activity"],
      3: ["active", "display", "lineAbove", "export"],
      4: ["apGroup"],
    };

    // Only add these fields if apGroup is true
    if (formik.values.apGroup === true) {
      structure[4] = ["apGroup", "useFormula", "displayChildren"];

      if (formik.values.useFormula === true) {
        structure[5] = ["formula"];
      } else {
        structure[5] = ["accountParameters"];
      }
      structure[6] = ["colorText", "colorBackground"];
    } else {
      structure[5] = ["colorText", "colorBackground"];
    }

    return structure;
  };

  // Generate the custom structure based on current values
  const updateCustomStructure = () => {
    const structure = getCustomStructure();
    setCustomStructure(structure);
  };

  formik["special"] = {
    placeholder: {
      accountParameters: "Select one account or more",
      type: "Select a type",
      activity: "Select an activity",
    },
    helperText: {
      accountParameters: "Account to be associated (the sum will be realized)",
    },
    customStructure: customStructure,
    disabled: {
      // Liste des champs à désactiver
      accountNumber: editData ? true : false,
      name: editData ? true : false,
    },
    customComponents: {
      formula: (
        <MentionInput
          value={formik.values.formula}
          onChange={(value) => formik.setFieldValue("formula", value)}
          label="Formula"
          helperText={`
            Start by typing @ to see the available accounts.
            You can also use the following operators: + - * / ( ).
            Ex: @AGG LAB COST Basic * @AGG LAB COST Overtime
          `}
          endpoint={_ROUTES.ACCOUNTS}
          activity={formik.values.activity}
          displayField="name"
          extraFields={["accountNumber"]}
          entityType="account"
          renderSuggestion={(suggestion, search, highlightedDisplay) => (
            <div>
              <strong>{suggestion.display}</strong>
              {suggestion.accountNumber && ` (${suggestion.accountNumber})`}
            </div>
          )}
        />
      ),
    },
    autocomplete: {
      accountParameters: {
        optionLabel: "name",
        optionValue: "id",
        multiple: true,
      },
    },
  };

  const {
    rowCount,
    data,
    isLoaded,
    isError,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    isLoading,
    setRefresh,
  } = useContext(BasicListContext);

  // Get the table configuration
  const c = getMuiTableConfig(
    // Params
    { enableColumnFilters: true, enableExpanding: true },
    // States & setStates (from context) Have to be the same as this
    {
      rowCount,
      isLoaded,
      isError,
      pagination,
      setPagination,
      columnFilters,
      setColumnFilters,
      globalFilter,
      setGlobalFilter,
      sorting,
      setSorting,
      isLoading,
    },
  );

  const deleteData = (data) => {
    createConfirmToast(
     "warning",
     "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel",
      "#d33",
      "#3085d6"
    ).then((result) => {
      if (result) {
        rq.doRequest(_ACTIONS.DELETE, _ROUTES.ACCOUNTS + `/${data.id}`).then((res) => {
          if (res.status === 200 || res.status === 204) {
          
            createToast(_SWAL_ICONS.success, "Account deleted successfully").then(() => {
              setRefresh((prev) => prev + 1);
            });
          } else {
            createToast(_SWAL_ICONS.error, res.message);
          }
        });
      }
    });
  };

    const cloneData = (data) => {
    createConfirmToast(
     "warning",
     "Are you sure?",
      "The name will be the same + 'copy'!",
      "Yes, copy it!",
      "Cancel",
      "#d33",
      "#3085d6"
    ).then((result) => {
      if (result) {
        rq.doRequest(_ACTIONS.GET, _ROUTES.ACCOUNTS_COPY + `/${data.id}`).then((res) => {
          if (res.status === 200 || res.status === 204) {

            createToast(_SWAL_ICONS.success, "Account copied successfully").then(() => {
              setRefresh((prev) => prev + 1);
            });
          } else {
            createToast(_SWAL_ICONS.error, res.data.message);
          }
        });
      }
    });
  };

  const columns = AccountsListColumns({
    setEditData,
    setShowModal,
    deleteData,
    cloneData,
  });

  const table = useMaterialReactTable({
    columns: columns,
    data: data,
    ...c,
    enableColumnResizing: true,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    initialState: {
      density: "compact",
    },
  });

  useEffect(() => {
    if (editData) {
      const accountAssociate = [];

      if (editData.accountParameters && editData.accountParameters.length > 0) {
        editData.accountParameters.map((el) => {
          accountAssociate.push({
            value: el["@id"],
            label: el.name,
          });
        });
      }

      const values = {
        name: editData.name,
        accountNumber: editData.accountNumber,
        apOrder: editData.apOrder,
        type: editData.type,
        activity: editData.activity ? editData.activity["@id"] : "",
        active: editData.active,
        display: editData.display,
        lineAbove: editData.lineAbove,
        export: editData.export,
        apGroup: editData.apGroup,
        useFormula: editData.useFormula,
        displayChildren: editData.displayChildren,
        accountParameters: accountAssociate,
        formula: editData.formula || "",
        colorText: editData.colorText,
        colorBackground: editData.colorBackground,
      };

      formik.setValues(values);
    } else {
      formik.resetForm();
    }
  }, [editData]);

  useEffect(() => {
    // Initial setup of validation schema and custom structure
    updateValidationSchema();
    updateCustomStructure();

    let t = [];

    if (options.type.length === 0) {
      dataService.fetchData(
        _ROUTES.ACCOUNT_PARAMETER_TYPES,
        {
          pagination: false,
          active: 1,
          "order[name]": "ASC",
          "groups[]": "select:get",
        },
        (data) => {
          t["type"] = buildOptions(data, "value", "value");
        },
      );
    }

    if (options.activity.length === 0) {
      dataService.fetchData(
        _ROUTES.ACTIVITIES,
        {
          pagination: false,
          active: 1,
          "order[name]": "ASC",
          "groups[]": "select:get",
        },
        (data) => {
          t["activity"] = buildOptions(data, "name", "@id");
        },
      );
    }

    if (options.accountParameters.length === 0) {
      dataService.fetchData(
        _ROUTES.ACCOUNTS,
        {
          pagination: false,
          "exists[accountNumber]": 1,
          "order[name]": "ASC",
          "groups[]": "select:get",
        },
        (data) => {
          const d = [];
          for (const [key, value] of Object.entries(data)) {
            const el = value;

            d.push({
              value: el["@id"],
              label: el.name,
            });
          }

          t["accountParameters"] = d;

          setOptions(t);
        },
      );
    }
  }, []);

  useEffect(() => {
    if (customStructure) {
      formik.special = {
        ...formik.special,
        placeholder: {
          accountParameters: "Select one account or more",
          type: "Select a type",
          activity: "Select an activity",
        },
        helperText: {
          accountParameters:
            "Account to be associated (the sum will be realized)",
        },
        customStructure: customStructure,
        autocomplete: {
          accountParameters: {
            optionLabel: "name",
            optionValue: "id",
            multiple: true,
          },
        },
      };
    }
  }, [customStructure, editData]);

  // Update both schema and structure when apGroup changes
  useEffect(() => {
    updateValidationSchema();
    updateCustomStructure();
  }, [formik.values.apGroup, formik.values.useFormula]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: open ? window.innerWidth - 340 : window.innerWidth - 64,
          borderRadius: "1px",
          boxShadow: `0px 0px 2px ${_COLORS.grey} `,
          overflowX: "auto",
        }}
      >
        {/* Table Component is here */}
        <MuiTableComponent route={_ROUTES.ACCOUNTS} table={table} />
      </Box>
      {/* Modal  */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={"Account form"}
        content={
          <BasicFormikForm
            formik={formik}
            setEditData={setEditData}
            setShowModal={setShowModal}
            validations={validationSchema}
            options={options}
          />
        }
        icon={<AccountTreeIcon />}
        width={"600"}
      />
    </>
  );
};
